import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "../../components/ui";
import { useLdProgramStatus, useSuccessionMap, useTrainingNeeds } from "../../lib/api";
import { HS_PROGRAM, INDUCTION_PROGRAM, OJT_PROGRAM } from "./shared";

/** Standard programme progress (WP-23.1): the training-needs count, induction, health & safety, on-the-job
 * training, and how many critical roles have somebody ready now. */
export default function Programmes({ company }: { company?: string }) {
  const { t } = useTranslation();
  const tna = useTrainingNeeds(company, true);
  const induction = useLdProgramStatus(INDUCTION_PROGRAM, company, true);
  const hs = useLdProgramStatus(HS_PROGRAM, company, true);
  const ojt = useLdProgramStatus(OJT_PROGRAM, company, true);
  const succession = useSuccessionMap(company, true);
  const readyRoles = useMemo(() => (succession.data ?? []).filter((s) => s.candidates.some((c) => c.band === "Ready now")).length, [succession.data]);

  return (
    <div data-ld-programmes>
      {/* programme dashboard summary */}
      <section className="mt-6">
        <h2 className="font-heading text-base font-semibold">{t("ld.dashboard")} <span className="text-xs font-normal text-brand-grey">· {t("ld.dashboardHint")}</span></h2>
        <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-5">
          <Card className="px-4 py-3"><div className="font-heading text-2xl font-semibold">{tna.data?.needing_training.length ?? "…"}</div><div className="text-[11px] uppercase tracking-wide text-brand-grey">{t("ld.needingTraining")}</div></Card>
          <Card className="px-4 py-3"><div className="font-heading text-2xl font-semibold">{induction.data ? `${induction.data.completion_pct} %` : "…"}</div><div className="text-[11px] uppercase tracking-wide text-brand-grey">{t("ld.programmes.induction")}</div></Card>
          <Card className="px-4 py-3"><div className="font-heading text-2xl font-semibold">{hs.data ? `${hs.data.completion_pct} %` : "…"}</div><div className="text-[11px] uppercase tracking-wide text-brand-grey">{t("ld.programmes.healthSafety")}</div></Card>
          <Card className="px-4 py-3"><div className="font-heading text-2xl font-semibold">{ojt.data ? `${ojt.data.completion_pct} %` : "…"}</div><div className="text-[11px] uppercase tracking-wide text-brand-grey">{t("ld.programmes.ojt")}</div></Card>
          <Card className="px-4 py-3"><div className="font-heading text-2xl font-semibold">{readyRoles}</div><div className="text-[11px] uppercase tracking-wide text-brand-grey">{t("ld.readyRoles")}</div></Card>
        </div>
      </section>
    </div>
  );
}
